import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { AuthProvider as OidcAuthProvider, useAuth } from 'react-oidc-context';
import type { AuthProviderProps } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';

import { getCognitoConfig } from './cognitoConfig';
import { registerAuthExpiredHandler, setAuthToken } from './authToken';

/**
 * Keeps the React-free API layer in sync with the OIDC session:
 *   - pushes the current access token into authToken.ts for api/client.ts, and
 *   - registers what to do when the API reports the session is invalid.
 */
function AuthSessionBridge({ children }: { children: ReactNode }) {
  const auth = useAuth();

  // Push the access token to the API layer synchronously during render, NOT in
  // an effect. React flushes a child's passive effects before its parent's, so
  // an effect here would let a child page's initial data loads
  // (loadSettings / loadSavedAddresses) run before the token is set and fail
  // with "API connection is not configured". setAuthToken only assigns a module
  // variable (no state update, no re-render), so calling it in render is safe.
  setAuthToken(auth.isAuthenticated ? auth.user?.access_token ?? null : null);

  useEffect(() => {
    // On an API auth failure, drop the local session so the gate falls back to
    // the sign-in screen. We deliberately do not auto-redirect, to avoid a
    // redirect loop on a transient 401.
    registerAuthExpiredHandler(() => {
      void auth.removeUser();
    });
    return () => registerAuthExpiredHandler(null);
  }, [auth]);

  return <>{children}</>;
}

/**
 * Wraps the app in the OIDC context when Cognito SSO is configured; otherwise a
 * transparent passthrough so the legacy shared-token flow is unaffected.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const config = getCognitoConfig();
  if (!config) return <>{children}</>;

  const settings: AuthProviderProps = {
    authority: config.authority,
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    post_logout_redirect_uri: config.postLogoutRedirectUri,
    response_type: 'code',
    scope: config.scope,
    // Send the Cognito identity_provider hint on every authorize request so the
    // Hosted UI goes straight to Google instead of showing a chooser.
    extraQueryParams: { identity_provider: config.idpIdentifier },
    automaticSilentRenew: true,
    // Match the existing security posture: tokens live only in this tab.
    userStore:
      typeof window !== 'undefined'
        ? new WebStorageStateStore({ store: window.sessionStorage })
        : undefined,
    // Strip ?code&state from the URL once the redirect is processed.
    onSigninCallback: () => {
      if (typeof window !== 'undefined') {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + window.location.hash,
        );
      }
    },
  };

  return (
    <OidcAuthProvider {...settings}>
      <AuthSessionBridge>{children}</AuthSessionBridge>
    </OidcAuthProvider>
  );
}
